// Advisor telemetry — fire-and-forget writes to decision_events
// (mig 20260707110000). DSS P0 of docs/specs/decision-support-system.md §5.1:
// every advisor exposure / acceptance / override / dismissal is one measured
// event, which is what lets an advisor be demoted or killed on evidence
// (§5.4 alert budget) instead of becoming permanent UI.
//
// Same hard rules as helpEvents.ts: this must NEVER block, throw, queue, or
// compete with the operational offline queue. So:
//   - dropped entirely when offline — telemetry is not worth a queue slot;
//   - not awaited; DB/network errors are swallowed (until the migration is
//     applied in an environment, every insert fails silently — by design);
//   - tenant_id / user_id are forced server-side by trigger; nothing sent
//     from here is trusted.

import { supabase } from '@/lib/supabase'

export type AdvisorVerdict = 'go' | 'caution' | 'nogo' | 'unknown'
export type DecisionEventType = 'shown' | 'accepted' | 'overridden' | 'dismissed'

export interface AdvisorRecommendation {
  verdict: AdvisorVerdict
  reasons: string[]
  /** Small aggregates behind the verdict (n, window, values) — never raw rows. */
  evidence?: Record<string, unknown>
}

export interface DecisionEventInput {
  advisorKey:      string
  eventType:       DecisionEventType
  entityType?:     string
  entityId?:       string
  recommendation:  AdvisorRecommendation
  /** What the operator actually chose — accepted/overridden events only. */
  actionTaken?:    Record<string, unknown>
  overrideReason?: string
}

// 'shown' throttle: one exposure per advisor+entity per tab session — the
// table measures exposures, not renders (spec §5.1). Deliberate/terminal
// events (accepted/overridden/dismissed) are never throttled.
const shownLogged = new Set<string>()

// Mirror the DB caps (advisor_key 60 / entity 40+80 / reason 300 / 2 KB
// JSONB) so a verbose caller degrades to a truncated row, not a lost one.
const capped = (s: string | null | undefined, max: number): string | null => {
  const t = s?.trim()
  return t ? t.slice(0, max) : null
}

/**
 * Advisor entity id for planting-scoped signals — the ONE builder, living
 * with the 80-char entity_id cap it must respect: `uuid:date:uuid` is 84
 * chars, so the planting rides as an 8-char uuid prefix (join back to
 * farm_projects by prefix-match; collision odds within one block+date are
 * negligible). Scoped and whole-block advisories on the same block+date are
 * DIFFERENT decisions and must not conflate in the event stream.
 */
export function advisorEntityId(blockId: string, ymd: string, projectId?: string | null): string {
  const scoped = projectId ? `:${projectId.slice(0, 8)}` : ''
  return `${blockId}:${ymd}${scoped}`
}

export function logDecisionEvent(input: DecisionEventInput): void {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return
  if (input.eventType === 'shown') {
    const key = `${input.advisorKey}:${input.entityId ?? ''}`
    if (shownLogged.has(key)) return
    shownLogged.add(key)
  }
  const row = {
    advisor_key: input.advisorKey.slice(0, 60),
    event_type:  input.eventType,
    entity_type: capped(input.entityType, 40),
    entity_id:   capped(input.entityId, 80),
    recommendation: {
      verdict: input.recommendation.verdict,
      reasons: input.recommendation.reasons.slice(0, 8).map(r => r.slice(0, 160)),
      ...(input.recommendation.evidence ? { evidence: input.recommendation.evidence } : {}),
    },
    action_taken:    input.actionTaken ?? null,
    override_reason: capped(input.overrideReason, 300),
  }
  void supabase.from('decision_events').insert(row).then(() => {}, () => {})
}
